import React from 'react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ReferenceLine
} from 'recharts';
import { Box, Typography, useTheme } from '@mui/material';

const KpiChart = ({ 
  data, 
  title, 
  unit, 
  chartType = 'line',
  height = 300,
  showGrid = true,
  colors = ['#1976d2', '#ff9800'],
  targetValue
}) => {
  const theme = useTheme();
  
  if (!data || !data.length) {
    return (
      <Box 
        sx={{ 
          height: typeof height === 'string' && height === '100%' ? '100%' : height,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#f5f5f5',
          borderRadius: 2,
          border: '1px dashed #ccc',
          minHeight: typeof height === 'string' && height === '100%' ? 200 : height
        }}
      >
        <Typography variant="body2" color="textSecondary">
          데이터가 없습니다
        </Typography>
      </Box>
    );
  }

  // 데이터 정규화 함수
  const normalizeChartData = (rawData) => {
    if (typeof rawData === 'string') {
      rawData = JSON.parse(rawData);
    }
    
    // 모든 설비명(키) 추출
    const equipmentNames = [];
    if (rawData.length > 0) {
      const firstItem = rawData[0];
      const equipmentKeys = Object.keys(firstItem).filter(key => 
        key !== 'name' && 
        key !== 'date' && 
        key !== '__typename' && 
        typeof firstItem[key] === 'number'
      );
      equipmentNames.push(...equipmentKeys);
    }
    
    return {
      data: rawData.map(item => {
        // date는 name 필드에서 가져오기
        const date = item.name || item.date;
        
        // 각 설비별 값들을 모두 포함
        const result = { date: date };
        
        equipmentNames.forEach(equipmentName => {
          result[equipmentName] = item[equipmentName] || 0;
        });
        
        // 목표값 추가 (있는 경우)
        if (targetValue && targetValue > 0) {
          result['목표값'] = targetValue;
        }
        
        return result;
      }),
      equipmentNames: equipmentNames
    };
  };

  const normalizedResult = normalizeChartData(data);
  const chartData = normalizedResult.data;
  const equipmentNames = normalizedResult.equipmentNames;
  
  // 설비별 색상 배열 (더 많은 색상 추가)
  const equipmentColors = [
    '#1976d2', '#ff9800', '#4caf50', '#f44336', '#9c27b0',
    '#00bcd4', '#795548', '#607d8b', '#e91e63', '#3f51b5'
  ];
  
  const formatTooltip = (value, name) => {
    if (typeof value === 'number') {
      return [
        `${value.toLocaleString()} ${unit || ''}`,
        name === '목표값' ? '목표값' : name
      ];
    }
    return [value, name];
  };

  const formatYAxisTick = (value) => {
    if (typeof value === 'number') {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(1)}K`;
      }
    }
    return value;
  };

  // X축 날짜 포맷 함수
  const formatXAxisTick = (tickItem) => {
    if (!tickItem) return '';
    try {
      const date = new Date(tickItem);
      // 유효한 날짜인지 확인
      if (isNaN(date.getTime())) {
        return tickItem; // 원본 값 반환
      }
      // 일별 데이터의 경우 시간까지 표시
      if (tickItem.includes(':')) {
        return tickItem; // 이미 시간 형식이면 그대로 사용
      }
      return `${date.getMonth() + 1}/${date.getDate()}`;
    } catch (error) {
      return tickItem; // 에러 시 원본 값 반환
    }
  };

  const commonProps = {
    width: '100%',
    height: height,
    data: chartData,
    margin: { top: 10, right: 10, left: 0, bottom: 10 }
  };

  const renderChart = () => {
    switch (chartType) {
      case 'area':
        return (
          <AreaChart {...commonProps}>
            <defs>
              <linearGradient id="colorActual" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[0]} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={colors[0]} stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="colorTarget" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor={colors[1]} stopOpacity={0.3}/>
                <stop offset="95%" stopColor={colors[1]} stopOpacity={0}/>
              </linearGradient>
            </defs>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis 
              dataKey="date" 
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={formatXAxisTick}
              angle={-45}
              textAnchor="end"
              height={40}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={formatYAxisTick}
              label={{ 
                value: unit, 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: theme.palette.text.primary, fontSize: 12, fontWeight: 600 }
              }}
            />
            <Tooltip formatter={formatTooltip} />
            <Legend />
            {equipmentNames.map((equipmentName, index) => (
              <Area
                key={equipmentName}
                type="monotone"
                dataKey={equipmentName}
                stroke={equipmentColors[index % equipmentColors.length]}
                fillOpacity={0.3}
                fill={equipmentColors[index % equipmentColors.length]}
                strokeWidth={2}
                name={equipmentName}
              />
            ))}
            {targetValue && targetValue > 0 && (
              <ReferenceLine 
                y={targetValue} 
                stroke="#ff5722" 
                strokeDasharray="8 8"
                strokeWidth={2}
              />
            )}
          </AreaChart>
        );
        
      case 'bar':
        return (
          <BarChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis 
              dataKey="date" 
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={formatXAxisTick}
              angle={-45}
              textAnchor="end"
              height={40}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={formatYAxisTick}
              label={{ 
                value: unit, 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: theme.palette.text.primary, fontSize: 12, fontWeight: 600 }
              }}
            />
            <Tooltip formatter={formatTooltip} />
            <Legend />
            {equipmentNames.map((equipmentName, index) => (
              <Bar 
                key={equipmentName}
                dataKey={equipmentName} 
                fill={equipmentColors[index % equipmentColors.length]}
                name={equipmentName}
                radius={[4, 4, 0, 0]}
              />
            ))}
            {targetValue && targetValue > 0 && (
              <ReferenceLine 
                y={targetValue} 
                stroke="#ff5722" 
                strokeDasharray="8 8"
                strokeWidth={2}
              />
            )}
          </BarChart>
        );
        
      default: // line
        return (
          <LineChart {...commonProps}>
            {showGrid && <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />}
            <XAxis 
              dataKey="date" 
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={formatXAxisTick}
              angle={-45}
              textAnchor="end"
              height={40}
            />
            <YAxis 
              stroke={theme.palette.text.secondary}
              fontSize={12}
              tickFormatter={formatYAxisTick}
              label={{ 
                value: unit, 
                angle: -90, 
                position: 'insideLeft',
                style: { fill: theme.palette.text.primary, fontSize: 12, fontWeight: 600 }
              }}
            />
            <Tooltip formatter={formatTooltip} />
            <Legend />
            {equipmentNames.map((equipmentName, index) => (
              <Line
                key={equipmentName}
                type="monotone"
                dataKey={equipmentName}
                stroke={equipmentColors[index % equipmentColors.length]}
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6, fill: equipmentColors[index % equipmentColors.length] }}
                name={equipmentName}
              />
            ))}
            {targetValue && targetValue > 0 && (
              <ReferenceLine 
                y={targetValue} 
                stroke="#ff5722" 
                strokeDasharray="8 8"
                strokeWidth={2}
              />
            )}
          </LineChart>
        );
    }
  };

  return (
    <Box sx={{ 
      width: '100%', 
      height: typeof height === 'string' && height === '100%' ? '100%' : 'auto',
      display: 'flex',
      flexDirection: 'column'
    }}>
      {title && (
        <Typography 
          variant="h6" 
          sx={{ 
            mb: 2, 
            fontWeight: 600,
            color: theme.palette.text.primary,
            textAlign: 'center'
          }}
        >
          {title}
        </Typography>
      )}
      <Box sx={{ 
        flex: typeof height === 'string' && height === '100%' ? 1 : 'none',
        minHeight: typeof height === 'string' && height === '100%' ? 200 : height
      }}>
        <ResponsiveContainer width="100%" height="100%">
          {renderChart()}
        </ResponsiveContainer>
      </Box>
    </Box>
  );
};

export default KpiChart;